<script setup>
import AuthConsoleCard from '@/components/auth/AuthConsoleCard.vue';
import KHKNotificationCard from '@/components/ui/KHKNotificationCard.vue';

import authService from '@/services/AuthService.js';
import { useEnvironment } from '@/composables/useEnvironment';
import { useAuth } from '@okta/okta-vue';
import { Button } from 'primevue';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const $auth = useAuth();

const errorMessage = ref('');
const loading = ref(false);

// Dan only shows up on the non-prod playgrounds (local + QA), never on
// staging/production.
const { environment } = useEnvironment();
const showDan = computed(() => environment === 'local' || environment === 'qa');

// Dan's rotating words of wisdom. Add/edit freely.
const danPhrases = [
    'Foot guns have been automatically upgraded to foot cannons!',
    'A metric **** ton of work done in here!',
    'Officially stamped LGTM!',
    'No babies thrown out with bathwater, promise!',
    '\"Teamwork makes the dream work.\" - Gary Busey',
    'This feature is classic Wargen over-engineering.',
    'It\'s about a 70% chance that my equipment just ***** up.',
    'We have come to it and the bridge is now crossed.',
    'People don\'t leave bad reviews on app stores. They leave bad reviews to regulators.',
    'QA and staging are running on a potato. And not even a good potato.',
    'We\'re going to add Easter eggs to the site and not tell Jeff.',
    'One of the benefits of being small is we are nimble.',
    'I can\'t blame Even Bet anymore? I don\'t like any part of this.',
    'No news is good news!',
    'The greatest evil AI brought into this world is that it made us all middle managers.',
    'It\'s not a cluster ****. Full stop.',
    'Ain\'t nothing to it but to do it.',
    'Looks solid to me!',
    'I\'ll fix that right up for ya',
    'Another week in the books',
    'This release included a metric ****-ton of work',
    'I think this release landed softly?'
];

const danPhrase = ref(danPhrases[0]);
// Loaded lazily (dynamic import) so the ~178KB image is a separate chunk
// fetched only on local/QA, never bundled into prod's main entry.
const danImg = ref(null);
// The bubble stays hidden until Dan finishes rising into place, so the quote
// doesn't hang in mid-air before he's arrived.
const showBubble = ref(false);
let danIndex = 0;
let danTimer = null;
let danRevealTimer = null;

// Matches the dan-enter animation duration (0.85s) plus a small beat so the
// bubble pops after he's settled.
const DAN_ENTER_MS = 950;
// Each phrase is shown for 8s, then the bubble hides for a 2s gap before the
// next one fades in.
const DAN_VISIBLE_MS = 8000;
const DAN_GAP_MS = 2000;

// Advance to a random phrase, never repeating the one just shown.
function pickNextPhrase() {
    if (danPhrases.length > 1) {
        let next = danIndex;
        while (next === danIndex) {
            next = Math.floor(Math.random() * danPhrases.length);
        }
        danIndex = next;
    }
    danPhrase.value = danPhrases[danIndex];
}

// One show/hide cycle: keep the current phrase up, then hide it, swap to a new
// one while hidden, and fade it back in — repeating for as long as Dan is around.
function scheduleDanCycle() {
    danTimer = setTimeout(() => {
        showBubble.value = false; // fade out, starting the 2s gap
        danTimer = setTimeout(() => {
            pickNextPhrase();
            showBubble.value = true; // fade the next phrase in
            scheduleDanCycle();
        }, DAN_GAP_MS);
    }, DAN_VISIBLE_MS);
}

// Clicking Dan skips to the next phrase right away and restarts the timer, so
// the fresh phrase gets its full time on screen instead of vanishing mid-read.
function advanceDanPhrase() {
    if (!showDan.value) return;
    if (danRevealTimer) clearTimeout(danRevealTimer);
    if (danTimer) clearTimeout(danTimer);
    pickNextPhrase();
    showBubble.value = true;
    scheduleDanCycle();
}

async function startDan() {
    if (!showDan.value) return;
    danImg.value = (await import('@/assets/console-mascot.png')).default;
    // Start on a random phrase so it isn't always the same one on load.
    danIndex = Math.floor(Math.random() * danPhrases.length);
    danPhrase.value = danPhrases[danIndex];
    // Wait for Dan's entrance before revealing the bubble and kicking off the
    // rotation, so the quote appears only once he's in place.
    danRevealTimer = setTimeout(() => {
        showBubble.value = true;
        scheduleDanCycle();
    }, DAN_ENTER_MS);
}

// The logged-out login screen is brand-neutral and carries a fixed KHK ADMIN
// PORTAL identity (an ops-terminal treatment, #2230). It no longer has a
// hardcoded brand picker: the admin logs straight into their last-used brand and
// switches brands on demand from the profile menu (SelectBrandView), which lists
// the real admin-selectable brands from the backend, never a hardcoded list
// (#2230, #2122).
async function handleOktaLogin() {
    loading.value = true;
    errorMessage.value = '';

    try {
        console.log('Initiating Okta login redirect...');
        await $auth.signInWithRedirect();
    } catch (error) {
        console.error('Okta login error:', error);
        errorMessage.value = error.message || 'Failed to initiate login. Please try again.';
        loading.value = false;
    }
}

onMounted(async () => {
    const isAuthenticated = await $auth.isAuthenticated();

    if (isAuthenticated || authService.isAuthenticated()) {
        console.log('User already authenticated, redirecting to dashboard');
        router.push({ name: 'dashboard' });
        return;
    }

    startDan().catch(error => console.warn('Failed to load console mascot:', error));
});

onUnmounted(() => {
    if (danTimer) clearTimeout(danTimer);
    if (danRevealTimer) clearTimeout(danRevealTimer);
});
</script>

<template>
    <AuthConsoleCard>
        <!-- Dan peeks out the top-right corner on local + QA only, dispensing
             rotating wisdom from a speech bubble. -->
        <div v-if="showDan && danImg" class="dan">
            <Transition name="dan-quote" mode="out-in">
                <div v-if="showBubble" class="dan-bubble" :key="danPhrase">{{ danPhrase }}</div>
            </Transition>
            <img :src="danImg" alt="Dan" class="dan-img" @click="advanceDanPhrase" />
        </div>

        <KHKNotificationCard v-if="errorMessage" :notification="{ message: errorMessage }" type="error"
            class="w-full mt-4" />

        <h1 class="con-signin">Sign in</h1>

        <Button @click="handleOktaLogin" :loading="loading" :disabled="loading" class="auth-button w-full">
            <template #default>
                <div class="button-content">
                    <div class="okta-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                            <path
                                d="M12 0C5.389 0 0 5.389 0 12s5.389 12 12 12 12-5.389 12-12S18.611 0 12 0zm0 18c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6z" />
                        </svg>
                    </div>
                    <span>{{ loading ? 'Redirecting...' : 'Continue with Okta' }}</span>
                    <i v-if="!loading" class="bi bi-arrow-right"></i>
                </div>
            </template>
        </Button>

        <div class="con-foot">© {{ new Date().getFullYear() }} KHK Games. All rights reserved.</div>
    </AuthConsoleCard>
</template>

<style scoped>
/* The .dan box is sized to Dan himself and positioned relative to
   .auth-console-frame (the popup), the nearest positioned ancestor. z-index: -1
   tucks him BEHIND the card (the frame owns the stacking context, the card isn't
   one of its own), so he peeks out from behind the top-right corner. The whole
   rig bobs gently via dan-float; the bubble is positioned absolutely off this box
   so it never pushes Dan around. */
.dan {
    position: absolute;
    top: -166px;
    right: -14px;
    z-index: -1;
    width: 200px;
    height: 200px;
    pointer-events: none;
    /* Rise up from behind the card (dan-enter, runs once), then settle into the
       gentle idle bob (dan-float, infinite). dan-float is listed last so it wins
       the shared `transform` once its delay elapses and the entrance is done. */
    animation:
        dan-enter 0.85s cubic-bezier(0.22, 1, 0.36, 1) both,
        dan-float 3.6s ease-in-out 0.85s infinite;
}

.dan-img {
    display: block;
    width: 200px;
    height: 200px;
    object-fit: contain;
    filter: drop-shadow(0 6px 16px rgba(0, 0, 0, 0.45));
    transform: rotate(10deg);
    /* The .dan rig has pointer-events: none so it never blocks the card; re-enable
       just the image so clicking Dan skips to the next phrase. */
    pointer-events: auto;
    cursor: pointer;
}

/* Cream speech bubble, positioned absolutely above Dan and centered over him.
   `bottom` controls how far it sits above his head (raise the subtrahend to drop
   it closer). Centered via translateX(-50%), which the transitions preserve. */
.dan-bubble {
    position: absolute;
    bottom: calc(100% - 10px);
    left: 50%;
    transform: translateX(-50%);
    width: 240px;
    min-height: 3em;
    /* wide + a reserved two-line height so phrases land on ~2 lines. */
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.85rem 1.05rem;
    border-radius: 18px;
    background: linear-gradient(180deg, #fdf8e9 0%, #f2e4bf 100%);
    border: 3px solid #a97b3c;
    border-bottom-width: 9px;
    box-shadow:
        0 8px 20px rgba(0, 0, 0, 0.28),
        inset 0 2px 2px rgba(255, 255, 255, 0.8),
        inset 0 -5px 9px rgba(122, 92, 40, 0.14);
    font-family: Helvetica, Arial, 'Helvetica Neue', sans-serif;
    font-size: 13px;
    line-height: 1.5;
    color: #4a3a1c;
    /* Letterpress: a light shadow just below each glyph makes the text read as
       pressed into the cream surface. */
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.85);
    text-align: center;
}

.dan-quote-enter-active,
.dan-quote-leave-active {
    transition: opacity 0.35s ease, transform 0.35s ease;
}

.dan-quote-enter-from {
    opacity: 0;
    transform: translateX(-50%) translateY(6px);
}

.dan-quote-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(-6px);
}

/* Dan starts tucked fully behind the card (translated down past his poke-out
   height, so the opaque card masks him) and rises into his resting spot, with a
   small overshoot so he pops up and settles rather than gliding to a dead stop. */
@keyframes dan-enter {
    0% {
        transform: translateY(200px);
        opacity: 0;
    }

    40% {
        opacity: 1;
    }

    65% {
        transform: translateY(-12px);
    }

    82% {
        transform: translateY(4px);
    }

    100% {
        transform: translateY(0);
        opacity: 1;
    }
}

@keyframes dan-float {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-14px);
    }
}

@media (prefers-reduced-motion: reduce) {
    .dan {
        animation: none;
    }
}
</style>
